import { logError } from "@/composables";
import { showConfirm, showToast } from "@/composables/swal";
import {
    ActivityItem,
    ApiResponse,
    ApiResponse2,
    Company,
    DashboardRequest,
    DashboardResult,
    DashboardTab,
    Factory,
    LockerRoom,
    MenuItem,
    MerchantUsers,
    NavigationMenuItem,
    Order,
    OrderAdminUser,
    OrderDetail,
    Result,
    Role,
    User,
} from "@/models";
import { AxiosResponse } from "axios";
import { createPinia, defineStore } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import { createApp, reactive, ref } from "vue";
import {
    handleResponseException,
    http,
} from "../interceptors/axiosRequestInterceptor";

const app = createApp({});
const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);
app.use(pinia);
type Urgency = 'vip' | 'urgent' | 'extra_attention';

export const useMainStore = defineStore("main", {
    state: () => ({
        navigation: ref<NavigationMenuItem[]>([]),
        user: ref<User | undefined>(undefined),
        loading: ref<boolean>(false),
        loadingNavigation: ref<boolean>(false),

        routeInfo: ref<NavigationMenuItem | null>(null),
        permissions: ref<Array<string> | undefined>([]),
        mobileDevice: ref<boolean>(false),
        sidebarOpen: ref<boolean>(true),
        role: ref<Role | undefined>(undefined),
        activity_item: ref<ActivityItem>(null),
        dashboardData: reactive<DashboardResult>({
            order_items_by_status_group: [],
            items: {
                current_page: 1,
                data: [],
                first_page_url: "",
                from: 0,
                last_page: 0,
                last_page_url: "",
                links: [],
                next_page_url: "",
                path: "",
                per_page: "",
                prev_page_url: "",
                to: 0,
                total: 0,
            },
            tab_count: {
                pending_merchant_approval_items_count: 0,
                pending_for_factory_assignment_count: 0,
                urgent_action_items_count: 0,
                customer_await_items_count: 0,
                quote_requested_items_count: 0,
                pending_items_count: 0,
                completed_items_count: 0,
                cancelled_items_count: 0,
                shipped_items_count: 0,
                reorders_count: 0,
                unresolved_comments_count: 0,
                all_items_count: 0,
            },
        }),
        dashboardTabs: ref<DashboardTab[]>([]),
        dashboardRequest: reactive<DashboardRequest>({
            items_for: "all_items",
            urgent_orders: null,
            sort_by: "order_no",
            sort_order: "desc",
            page: 1,
            search: "",
            per_page: 10,
        }),
        permissionLoaded: ref<boolean>(false),
        factories: ref<Factory[]>([]),
        orderAdmins: ref<OrderAdminUser[]>([]),
        lockerRoom: ref<LockerRoom[] | undefined>([]),
        merchantUsers: ref<MerchantUsers[]>([]),
        unassignedOrders: ref<Order[]>([]),
        companies: ref<Company[]>([]),
        customers: ref<[]>([]),
        dashboardQuickFilters: ref<Record<
            string,
            string | Array<string | number> | Record<"to" | "from", string>
        > | null>(null),
    }),
    actions: {
        async getNavigation() {
            this.loadingNavigation = true;
            try {
                const response: AxiosResponse<ApiResponse<MenuItem[]>> =
                    await http.get("navigation");
                const navigationMenu: NavigationMenuItem[] = [];
                response.data.data.forEach((data: MenuItem) => {
                    let currentMenu: NavigationMenuItem = {
                        name: "",
                        to: "",
                        icon: "",
                        children: [],
                    };
                    if (data.children_menu.length === 0) {
                        currentMenu.name = data.name;
                        currentMenu.to = data.url_path;
                    } else {
                        currentMenu.name = data.name;
                        currentMenu.to = data.url_path;
                        currentMenu.children = data.children_menu.map(
                            (item) => ({
                                name: item.name,
                                to: item.url_path,
                            }),
                        );
                    }
                    navigationMenu.push(currentMenu);
                });

                this.navigation = navigationMenu;
            } catch (err: any) {
                handleResponseException(err);
            } finally {
                this.loadingNavigation = false;
            }
        },
        async getCurrentUser() {
            this.loading = true;
            try {
                const userResponse: AxiosResponse<ApiResponse2<Result<User>>> =
                    await http.get("authuser");
                if (userResponse.status === 200) {
                    this.user = userResponse.data.result.user;
                    this.role = this.user?.roles[0];
                }
            } catch (err: any) {
                handleResponseException(err);
            }
            this.loading = false;
        },
        async getPermissions() {
            this.permissionLoaded = false;
            try {
                const permissions_response: AxiosResponse<
                    ApiResponse2<Result<Array<string>>>
                > = await http.get(`permissions`);
                if (permissions_response) {
                    const response_data = permissions_response.data;
                    this.permissions = response_data.result.permissions;
                    this.permissionLoaded = true;
                }
            } catch (err: any) {
                handleResponseException(err);
            }
        },
        async getDashboardData(
            data: DashboardRequest,
            payload: Record<
                string,
                string | Array<string | number> | Record<any, any>
            > | null = null,
        ) {
            this.dashboardRequest = data;
            this.loading = true;
            try {
                const queryParams = new URLSearchParams(data as any).toString();

                const requestBody = payload ? { quick_filters: payload } : {};
                const response: AxiosResponse<ApiResponse2<DashboardResult>> =
                    await http.post(`dashboard?${queryParams}`, requestBody);
                if (response) {
                    const response_data = response.data;
                    this.dashboardData = response_data.result;
                    this.dashboardTabs = Object.entries(
                        this.dashboardData.tab_count,
                    ).map(([key, count]) => {
                        const cleanedKey = key.replace(/_(items?|count)/gi, "");
                        return {
                            display_name: cleanedKey
                                .replace(/_/g, " ")
                                .replace(/\b\w/g, (char) => char.toUpperCase())
                                .trim(),
                            count,
                            key,
                            cleaned_key: cleanedKey,
                            apiKey: key.replace("_count", ""),
                        };
                    });
                }
            } catch (err: any) {
                this.loading = false;
                handleResponseException(err);
            }
            this.loading = false;
        },
        async getOrderDetail(order_id: number) {
            try {
                const response: AxiosResponse<ApiResponse2<OrderDetail>> =
                    await http.get(`customer-orders/${order_id}`);
                if (response) {
                    return response.data.result;
                }
            } catch (err: any) {
                handleResponseException(err);
            }
            return null;
        },

        async getFactories() {
            this.loading = true;
            try {
                const res = await http.get<ApiResponse<Factory[]>>(
                    "factory?paginate=false",
                );
                if (res.status === 200) {
                    this.factories = res.data.data;
                    this.loading = false;
                }
            } catch (err: any) {
                this.loading = false;
                handleResponseException(err);
            }
        },
        async getOrderAdmins() {
            this.loading = true;
            try {
                const res = await http.get<
                    ApiResponse2<Result<OrderAdminUser[]>>
                >("role/users?role=orderAdministrator");
                if (res.status === 200 && res.data.result.users) {
                    this.orderAdmins = res.data.result.users;
                }
                this.loading = false;
            } catch (err: any) {
                this.loading = false;
                handleResponseException(err);
            }
        },
        async getAllMerchants() {
            this.loading = true;
            try {
                const res =
                    await http.get<ApiResponse2<MerchantUsers[]>>(
                        "get-companies",
                    );
                if (res.status === 200 && res.data.result) {
                    this.merchantUsers = res.data.result;
                }
                this.loading = false;
            } catch (err: any) {
                this.loading = false;
                handleResponseException(err);
            }
        },

        async getUnassignedOrders() {
            this.loading = true;
            try {
                const res = await http.get<ApiResponse2<Result<Order[]>>>(
                    "get_order_administrator_unassinged_orders",
                );
                if (res.status === 200 && res.data.result.orders) {
                    this.unassignedOrders = res.data.result.orders;
                }
                this.loading = false;
            } catch (err: any) {
                this.loading = false;
                handleResponseException(err);
            }
        },

        async uploadCustomTechpack(files: FileList, order: Order) {
            try {
                const custom_techpack_file = files[0];
                const form_data = new FormData();
                form_data.append("custom_techpack_pdf", custom_techpack_file);
                form_data.append("order_id", order.id.toString());
                this.loading = true;
                const uploadTechpackResponse = await http.post(
                    "updload-custom-techpack",
                    form_data,
                    {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                    },
                );
                if (uploadTechpackResponse.status === 200) {
                    showToast(uploadTechpackResponse.data.message, "success");
                    this.loading = false;
                }
            } catch (errorResponse: any) {
                this.loading = false;
                handleResponseException(errorResponse);
            }
        },
        async notifyCustomerForQuote(order_id: number) {
            try {
                this.loading = true;
                const response = await http.get<ApiResponse2<any>>(
                    `notify-customer-quote/${order_id}`,
                );
                this.loading = false;
                if (response.data.message) {
                    showToast("Email has been sent to customer.", "success");
                }
            } catch (e: any) {
                this.loading = false;
                handleResponseException(e);
            }
        },
        async updateOrderFactory(payload: any) {
            this.loading = true;
            try {
                const { order_id } = payload;
                const res = await http.put(
                    `order/${order_id}/update-factory`,
                    payload,
                );
                showToast(res.data.message, "success");
                return res;
            } catch (error) {
                showToast("Error updating factory", "error");
                this.loading = true;
                throw error;
            }
        },
        async updateOrderUrgency(urgency: Urgency, orderId: number|string) {
            this.loading = true;
            try {
                const payload = {
                    order_id: orderId,
                    urgency: urgency
                }
                const res = await http.post(
                    `update/order/urgency`,
                    payload,
                );
                showToast(res.data.message, "success");
                return res;
            } catch (error) {
                this.loading = false;
                logError("Error downloading the file:", error);
            }
        },
        async exportOrderPrices(payload: {
            fromDate: string | null;
            endDate: string | null;
        }) {
            this.loading = true;
            try {
                const response = await http.post(
                    "/export-order-prices",
                    {
                        from_date: payload.fromDate,
                        end_date: payload.endDate,
                    },
                    {
                        responseType: "blob",
                    },
                );
                const url = window.URL.createObjectURL(
                    new Blob([response.data]),
                );
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", "order_prices.xlsx");
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                this.loading = false;
            } catch (error) {
                this.loading = false;
                logError("Error downloading the file:", error);
            }
        },
        async assignFactory(payload: any) {
            this.loading = true;
            try {
                const response = await http.post<ApiResponse2<any>>(
                    `/assign_factory`,
                    payload,
                );
                if (response.status === 200) {
                    this.loading = false;
                    if (response.data.message) {
                        showToast(response.data.message, "success");
                    }
                }
            } catch (err: any) {
                handleResponseException(err);
                showToast(err.response.data.message, "error");
                this.loading = false;
            }
        },
        async updateOrderAdmin(payload: {
            user_id: string | number | null;
            order_ids: number | any;
        }) {
            this.loading = true;
            try {
                const response = await http.post<ApiResponse2<any>>(
                    "assign_order_to",
                    payload,
                );
                if (response.status === 200) {
                    showToast("Order admin assigned", "success");
                    this.loading = false;
                } else {
                    showToast(response.data.message, "error");
                }
            } catch (err: any) {
                this.loading = false;
                showToast(err.response.data.message, "error");
                handleResponseException(err);
            }
            this.loading = false;
        },
        async deleteOrder(orderID: number | string) {
            let confirmDelete = await showConfirm(
                "",
                "Are you sure that you want to delete this order?",
            );
            if (confirmDelete) {
                try {
                    this.loading = true;

                    await http
                        .delete(`order/delete/${orderID}`)
                        .then(async (response) => {
                            if (
                                response.data.success &&
                                response.data.status_code === 200
                            ) {
                                showToast(response.data.message, "success");
                                this.loading = false;
                            } else {
                                this.loading = false;
                            }
                        });
                } catch (e) {
                    logError(e);
                    this.loading = false;
                }
            }
        },
        async getCustomerLockerRoom(companyId: number, customerId: number) {
            try {
                const response = await http.get<
                    ApiResponse2<Result<LockerRoom[]>>
                >(
                    `customer_lockers_rosters?company_id=${companyId}&customer_id=${customerId}`,
                );
                if (response.status === 200) {
                    this.lockerRoom =
                        response.data.result.customer_locker_rooms;
                }
                this.loading = false;
            } catch (err: any) {
                handleResponseException(err);
            }
        },
        async logout() {
            try {
                const response = await http.get("logout");
                if (response.status === 200) {
                    window.location.href =
                        import.meta.env.VITE_VUE2_APP_URL + "/login";
                }
            } catch (err: any) {
                handleResponseException(err);
            }
        },
    },
    getters: {},
    persist: {
        key: "dashboardRequest",
        pick: ["dashboardRequest"],
    },
});
